import "core-js/modules/es7.symbol.async-iterator";
import "core-js/modules/es6.symbol";
import "core-js/modules/es6.regexp.replace";
import "core-js/modules/es6.regexp.to-string";
import "core-js/modules/es6.object.to-string";

function _typeof(obj) { if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import lodashAssign from 'lodash/assign';
import HttpClientModule from './http-client';
import pollingContinue from './polling'; // Safe access to window global properties

function getWindowProp(property) {
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line jsx-control-statements/jsx-jcs-no-undef -- directive added automatically by Shepherd migration
    return window[property];
  }
}
/**
 * @typedef ServiceSDK
 * @type {object}
 * @property {function} del - Make a DELETE request.
 * @property {function} get - Make a GET request.
 * @property {function} http - Make an HTTP request.
 * @property {function} nwApiRoute - Construct an "official" NW-API route.
 * @property {function} patch - Make a PATCH request.
 * @property {function} poll - Wrapper around http for polling.
 * @property {function} post - Make an POST request.
 * @property {function} put - Make an PUT request.
 * @property {function} setKountSessionID
 * @property {function} setReferringClientID
 * @property {function} setUserLoggedIn
 * @property {function} setUserLoggedOut
 */

/**
 * Return an instance of ServiceSDK.
 *
 * ServiceSDK is a library for making calls to NW-API services.
 * @deprecated This is a legacy library. For HTTP making requests, use HttpClient instead.
 *
 * @param {object} config
 * @param {string} config.applicationID - Your FE app's unique application id, used to set a header.
 * @param {string} config.clientID - Your FE app's unique client id, used to set a header.
 * @param {boolean} [config.enableHeadersAndMeta]
 * @param {function} [config.getBrowserSessionIdOverride] - Custom browser id access logic.
 * @param {function} [config.getCookieIdOverride] - Custom cookie id access logic.
 * @param {function} [config.getCookies] - Custom cookie access logic.
 * @param {function} [config.getPageviewId] - Custom hook to get pageview id, used to set X-Pageview-ID header.
 * @param {function} [config.getUrl] - Custom hook to get current url, used to set X-Originating-Url header.
 * @param {HttpClient} [config.httpClient] - An instance of HttpClient
 * @param {function} [config.uuid] - Custom hook to get uuid.
 * @param {object}  config.nets - An instance of nets library.
 * @param {object} [config.nwa] - An instance of @nerdwallet/nwa.
 * @param {integer} [config.xhrTimeout] - Default timeout (milliseconds). Can be overridden on call to http().
 *
 * @return {ServiceSDK}
 */


var ServiceSDK = function ServiceSDK(config) {
  var DEFAULT_POLLING_TIMEOUT = 50000;
  var DEFAULT_POLLING_INTERVAL = 2000; // ---- Config ----

  var httpClient = config.httpClient || HttpClientModule(config); // ---- Interface ----
  // GET sugar on top of http.

  function get(url, params, callback, serverRequest, config) {
    http(lodashAssign({}, config, {
      method: 'GET',
      url: url,
      params: params,
      callback: callback,
      serverRequest: serverRequest
    }));
  } // POST sugar on top of http.


  function post(url, json, callback, serverRequest, config) {
    http(lodashAssign({}, config, {
      method: 'POST',
      url: url,
      json: json,
      callback: callback,
      serverRequest: serverRequest
    }));
  } // PUT sugar on top of http.


  function put(url, json, callback, serverRequest, config) {
    http(lodashAssign({}, config, {
      method: 'PUT',
      url: url,
      json: json,
      callback: callback,
      serverRequest: serverRequest
    }));
  } // PATCH sugar on top of http.


  function patch(url, json, callback, serverRequest, config) {
    http(lodashAssign({}, config, {
      method: 'PATCH',
      url: url,
      json: json,
      callback: callback,
      serverRequest: serverRequest
    }));
  } // DELETE sugar on top of http (del b/c delete is reserved in JS).


  function del(url, json, callback, serverRequest, config) {
    http(lodashAssign({}, config, {
      method: 'DELETE',
      url: url,
      json: json,
      callback: callback,
      serverRequest: serverRequest
    }));
  } // Wrapper around http for polling, should only be used client side


  function poll(apiRequest, timeout, interval) {
    // Default date.now - should never happen because we should always call this client side
    var pollingStartTime = Date.now();
    var pollingTimeout = timeout || DEFAULT_POLLING_TIMEOUT;
    var pollingInterval = interval || DEFAULT_POLLING_INTERVAL;

    if (getWindowProp('performance')) {
      // eslint-disable-next-line jsx-control-statements/jsx-jcs-no-undef -- directive added automatically by Shepherd migration
      pollingStartTime = window.performance.now();
    }

    pollingContinue(http, apiRequest, pollingTimeout, pollingInterval, pollingStartTime);
  }
  /**
   * @callback ServiceSDKCallback
   * @param {object} err
   * @param {Response} response
   * @param {object} body
   */

  /**
   * Make an HTTP request.
   *
   * If rendering server-side, pass in the current request object as args.serverRequest.
   *
   * @param {object} args
   * @param {object} [args.body] - Anything that can be passed to xhr.send. If `json` param is set, this will be overriden.
   * @param {ServiceSDKCallback} [args.callback]
   * @param {object} [args.cookies={}] - Additional cookies to be included in the request.
   *    These cookies will overwrite any cookies passed through the header argument.
   *    These cookies will be escaped prior to making the request.
   * @param {boolean} [args.followRedirect] - Accept followRedirect arg. This works server side only (node request).
   * @param {object} [args.headers={}] - Request headers.
   * @param {object} [args.json] - JSON request body (overrides `body` param if set).
   * @param {string} args.method - HTTP verb of request (GET|POST|PUT|DELETE).
   * @param {object} [args.params={}] - Request query parameters.
   * @param {Request} [args.serverRequest] - Current request if rendering server-side.
   * @param {integer} [args.timeout] - Request timeout in milliseconds.
   * @param {string} args.url - Request URL not including query params.
   *
   */


  function http(args) {
    var callback = args.callback ? args.callback : function () {};
    httpClient.fetch(args).then(function (result) {
      var err = result.err,
          response = result.response,
          body = result.body;
      callback(err, response, body);
    }).catch(function (e) {
      var response = e.response,
          body = e.body;
      callback(e, response, body);
    });
  } // Construct an "official" NW-API route.
  // WARNING: Must stay in sync with the route format in NW-API middleware.


  function nwApiRoute(baseUrl, namespace, version, endpoint) {
    var urlParts = [baseUrl, namespace, "v".concat(version.toString()), endpoint];
    var strippedParts = urlParts.map(function (p) {
      return p.replace(/^\//g, '');
    } // strip leading slashes
    );
    return strippedParts.join('/');
  }

  return {
    // Fetch functions
    get: get,
    post: post,
    put: put,
    patch: patch,
    del: del,
    poll: poll,
    http: http,
    // Other functions
    nwApiRoute: nwApiRoute,
    setKountSessionID: httpClient.setKountSessionID,
    setReferringClientID: httpClient.setReferringClientID,
    setUserLoggedIn: httpClient.setUserLoggedIn,
    setUserLoggedOut: httpClient.setUserLoggedOut
  };
};

export default function (configArg) {
  if (!configArg || _typeof(configArg) !== 'object') {
    throw new Error('config is required');
  }

  return ServiceSDK(configArg);
}